import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { fetchLiveUnloadPlanners } from './liveUnloadPlannerApi';

const PAGE_START = 1;
const SEARCH_DEBOUNCE_MS = 550;

export function useLiveUnloadPlannerApi(distanceLimitMi, containerSearch) {
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(PAGE_START);
  const [lastPage, setLastPage] = useState(1);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [listMeta, setListMeta] = useState(null);
  const [debouncedContainerSearch, setDebouncedContainerSearch] = useState(
    String(containerSearch || '').trim()
  );

  const loadingLock = useRef(false);
  const pageRef = useRef(PAGE_START);
  const lastPageRef = useRef(1);

  useEffect(() => {
    const nextSearch = String(containerSearch || '').trim();
    const t = setTimeout(() => {
      setDebouncedContainerSearch(nextSearch);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [containerSearch]);

  const queryKey = useMemo(
    () => ({
      distance: distanceLimitMi,
      container_number: debouncedContainerSearch,
    }),
    [distanceLimitMi, debouncedContainerSearch]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadFirst() {
      setInitialLoading(true);
      setError(null);
      pageRef.current = PAGE_START;
      lastPageRef.current = 1;
      try {
        const res = await fetchLiveUnloadPlanners({
          page: PAGE_START,
          distance: queryKey.distance,
          container_number: queryKey.container_number,
        });
        if (cancelled) return;
        setItems(res.cards);
        setCurrentPage(res.currentPage);
        setLastPage(res.lastPage);
        pageRef.current = res.currentPage;
        lastPageRef.current = res.lastPage;
        setListMeta(res.meta);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'something went wrong');
          setItems([]);
          setListMeta(null);
          setCurrentPage(PAGE_START);
          setLastPage(1);
          pageRef.current = PAGE_START;
          lastPageRef.current = 1;
        }
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    }

    loadFirst();
    return () => {
      cancelled = true;
    };
  }, [queryKey]);

  const loadNextPage = useCallback(async () => {
    if (loadingLock.current) return;
    if (pageRef.current >= lastPageRef.current) return;

    const nextPage = pageRef.current + 1;
    loadingLock.current = true;
    setLoadingMore(true);
    setError(null);
    try {
      const res = await fetchLiveUnloadPlanners({
        page: nextPage,
        distance: queryKey.distance,
        container_number: queryKey.container_number,
      });

      pageRef.current = res.currentPage;
      lastPageRef.current = res.lastPage;
      setItems((prev) => [...prev, ...res.cards]);
      setCurrentPage(res.currentPage);
      setLastPage(res.lastPage);
    } catch (e) {
      setError(e?.message || 'something went wrong');
    } finally {
      setLoadingMore(false);
      loadingLock.current = false;
    }
  }, [queryKey]);

  const loadNextPageRef = useRef(null);
  loadNextPageRef.current = loadNextPage;

  const hasMore = !initialLoading && currentPage < lastPage;

  return {
    items,
    initialLoading,
    loadingMore,
    hasMore,
    error,
    listMeta,
    loadNextPageRef,
    filteredTotal: items.length,
  };
}
